import { motion, useReducedMotion } from "framer-motion";
import { COMMENTARY_ART } from "@/data/content";

// Six neo-brutalist SVG posters, one per commentary moment.
// Animations play once on mount; reduced-motion renders the final state.

const VB = "0 0 800 500";
const DISPLAY = "'Archivo Black', sans-serif";
const MONO = "'JetBrains Mono', monospace";

function CaptainArt({ still, art }) {
  const rings = [60, 130, 200, 270];
  return (
    <svg viewBox={VB} className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="art-captain">
      <rect width="800" height="500" fill="#84E116" />
      {rings.map((r, i) => (
        <motion.circle
          key={r}
          cx="400"
          cy="250"
          r={r}
          fill="none"
          stroke="#0A0A0A"
          strokeWidth="6"
          style={{ transformOrigin: "400px 250px" }}
          initial={still ? false : { scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.9 }}
          transition={{ delay: 0.15 + i * 0.12, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        />
      ))}
      <motion.text
        x="400"
        y="330"
        textAnchor="middle"
        fontFamily={DISPLAY}
        fontSize="340"
        fill="#0A0A0A"
        initial={still ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1.3, 0.36, 1] }}
        style={{ transformOrigin: "400px 250px" }}
      >
        {art.glyph}
      </motion.text>
      <motion.rect
        x="-20"
        y="380"
        width="840"
        height="70"
        fill="#0A0A0A"
        initial={still ? false : { x: -860 }}
        animate={{ x: -20 }}
        transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      />
      <rect x="-20" y="380" width="840" height="70" fill="none" stroke="#84E116" strokeWidth="4" strokeDasharray="24 18" />
    </svg>
  );
}

function RealityArt({ still }) {
  // Ball falls along a dotted arc from the pink side onto the grid floor.
  const arc = [
    { x: 150, y: 90 },
    { x: 330, y: 170 },
    { x: 500, y: 300 },
    { x: 590, y: 395 },
  ];
  return (
    <svg viewBox={VB} className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="art-reality">
      <rect width="400" height="500" fill="#FFC7DA" />
      <rect x="400" width="400" height="500" fill="#E5E5E3" />
      {/* loose circles on pink */}
      <circle cx="110" cy="140" r="70" fill="none" stroke="#0A0A0A" strokeWidth="5" strokeDasharray="14 10" />
      <circle cx="230" cy="330" r="46" fill="none" stroke="#0A0A0A" strokeWidth="5" />
      <circle cx="300" cy="100" r="26" fill="none" stroke="#0A0A0A" strokeWidth="5" strokeDasharray="10 8" />
      {/* grid floor right half */}
      {[430, 470, 500].map((y) => (
        <line key={y} x1="400" y1={y} x2="800" y2={y} stroke="#0A0A0A" strokeWidth="3" opacity="0.5" />
      ))}
      {[460, 540, 620, 700, 780].map((x) => (
        <line key={x} x1={x} y1="400" x2={x} y2="500" stroke="#0A0A0A" strokeWidth="3" opacity="0.5" />
      ))}
      <line x1="400" y1="0" x2="400" y2="500" stroke="#0A0A0A" strokeWidth="6" />
      {/* dotted arc */}
      <path
        d="M 150 90 Q 330 170 590 395"
        fill="none"
        stroke="#0A0A0A"
        strokeWidth="4"
        strokeDasharray="2 16"
        strokeLinecap="round"
      />
      {/* falling football */}
      <motion.g
        initial={still ? false : { x: arc[0].x - 590, y: arc[0].y - 395, opacity: 0 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.5, 0, 0.75, 0.4] }}
      >
        <circle cx="590" cy="395" r="34" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="5" />
        <path d="M590 376 L604 387 L599 404 L581 404 L576 387 Z" fill="#0A0A0A" />
        <line x1="590" y1="376" x2="590" y2="361" stroke="#0A0A0A" strokeWidth="4" />
        <line x1="604" y1="387" x2="620" y2="380" stroke="#0A0A0A" strokeWidth="4" />
        <line x1="576" y1="387" x2="560" y2="380" stroke="#0A0A0A" strokeWidth="4" />
      </motion.g>
    </svg>
  );
}

function HalftimeArt({ still, art }) {
  return (
    <svg viewBox={VB} className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="art-halftime">
      <rect width="800" height="500" fill="#F5F1E8" />
      {/* chalk pitch lines */}
      <line x1="0" y1="250" x2="800" y2="250" stroke="#FFFFFF" strokeWidth="5" strokeDasharray="26 18" opacity="0.9" />
      <circle cx="400" cy="250" r="180" fill="none" stroke="#FFFFFF" strokeWidth="5" strokeDasharray="26 18" opacity="0.9" />
      {/* frozen clock */}
      <motion.text
        x="400"
        y="290"
        textAnchor="middle"
        fontFamily={MONO}
        fontWeight="800"
        fontSize="130"
        fill="#0A0A0A"
        initial={still ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {art.time}
      </motion.text>
      {/* empty circle with ? */}
      <motion.g
        initial={still ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        <circle cx="650" cy="110" r="62" fill="none" stroke="#0A0A0A" strokeWidth="5" strokeDasharray="16 12" />
        <text x="650" y="132" textAnchor="middle" fontFamily={DISPLAY} fontSize="56" fill="#0A0A0A">
          {art.mark}
        </text>
      </motion.g>
    </svg>
  );
}

function SearchArt({ still }) {
  const arrows = [
    { x: 90, y: 90, r: 0 },
    { x: 260, y: 70, r: 60 },
    { x: 430, y: 95, r: -45 },
    { x: 620, y: 80, r: 120 },
    { x: 730, y: 190, r: 90 },
    { x: 130, y: 250, r: -90 },
    { x: 300, y: 240, r: 150 },
    { x: 680, y: 330, r: -30 },
    { x: 110, y: 400, r: 45 },
    { x: 300, y: 420, r: -135 },
    { x: 700, y: 440, r: 160 },
  ];
  return (
    <svg viewBox={VB} className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="art-search">
      <rect width="800" height="500" fill="#F2F2F0" />
      {arrows.map((a, i) => (
        <motion.path
          key={i}
          d="M -38 0 L 22 0 M 8 -16 L 26 0 L 8 16"
          stroke="#0A0A0A"
          strokeWidth="9"
          fill="none"
          strokeLinecap="square"
          transform={`translate(${a.x} ${a.y}) rotate(${a.r})`}
          initial={still ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.05, duration: 0.25 }}
        />
      ))}
      {/* the blue arrow draws itself last */}
      <motion.path
        d="M 420 400 L 560 280 M 540 266 L 574 268 L 572 302"
        stroke="#0052FF"
        strokeWidth="16"
        fill="none"
        strokeLinecap="square"
        initial={still ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
}

function BuildingArt({ still }) {
  // Flat lightbulb: black outline, neon-green glass holding a top-down
  // pitch (centre circle + halfway line in white), black base below.
  return (
    <svg viewBox={VB} className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="art-building">
      <rect width="800" height="500" fill="#F2F2F0" />
      {/* three short rays */}
      <motion.g
        initial={still ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.4 }}
      >
        <line x1="400" y1="30" x2="400" y2="72" stroke="#0A0A0A" strokeWidth="10" />
        <line x1="215" y1="78" x2="243" y2="110" stroke="#0A0A0A" strokeWidth="10" />
        <line x1="585" y1="78" x2="557" y2="110" stroke="#0A0A0A" strokeWidth="10" />
      </motion.g>
      {/* bulb glass — outline draws in once */}
      <motion.path
        d="M 400 100
           C 322 100 260 162 260 240
           C 260 292 288 330 322 358
           L 322 390
           L 478 390
           L 478 358
           C 512 330 540 292 540 240
           C 540 162 478 100 400 100 Z"
        fill="#84E116"
        stroke="#0A0A0A"
        strokeWidth="10"
        strokeLinejoin="round"
        initial={still ? false : { pathLength: 0, fillOpacity: 0 }}
        animate={{ pathLength: 1, fillOpacity: 1 }}
        transition={{
          pathLength: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
          fillOpacity: { delay: 0.5, duration: 0.4 },
        }}
      />
      {/* top-down pitch inside the bulb — fades in after the outline */}
      <motion.g
        initial={still ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
      >
        <line x1="400" y1="118" x2="400" y2="372" stroke="#FFFFFF" strokeWidth="7" />
        <circle cx="400" cy="245" r="72" fill="none" stroke="#FFFFFF" strokeWidth="7" />
        <circle cx="400" cy="245" r="7" fill="#FFFFFF" />
      </motion.g>
      {/* base */}
      <motion.g
        initial={still ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.35 }}
      >
        <rect x="332" y="398" width="136" height="26" fill="#0A0A0A" />
        <rect x="346" y="432" width="108" height="22" fill="#0A0A0A" />
      </motion.g>
    </svg>
  );
}

function FulltimeArt({ still, art }) {
  return (
    <svg viewBox={VB} className="w-full h-full" preserveAspectRatio="xMidYMid slice" data-testid="art-fulltime">
      <rect width="800" height="500" fill="#0A0A0A" />
      {/* flat pitch horizon */}
      <line x1="0" y1="330" x2="800" y2="330" stroke="#84E116" strokeWidth="6" />
      <rect x="0" y="330" width="800" height="170" fill="#0F1A04" />
      {/* rising green sun */}
      <motion.circle
        cx="400"
        cy="330"
        r="120"
        fill="#84E116"
        initial={still ? false : { y: 260 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      />
      {/* giant FT */}
      <text x="120" y="290" fontFamily={DISPLAY} fontSize="150" fill="#FFFFFF">
        {art.mark}
      </text>
      {/* outlined score */}
      <motion.text
        x="680"
        y="440"
        textAnchor="middle"
        fontFamily={DISPLAY}
        fontSize="110"
        fill="none"
        stroke="#84E116"
        strokeWidth="3"
        initial={still ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        {art.score}
      </motion.text>
    </svg>
  );
}

const VARIANTS = {
  captain: CaptainArt,
  reality: RealityArt,
  halftime: HalftimeArt,
  search: SearchArt,
  building: BuildingArt,
  fulltime: FulltimeArt,
};

export default function CommentaryArt({ momentId }) {
  const still = useReducedMotion();
  const art = COMMENTARY_ART[momentId];
  const Variant = VARIANTS[momentId];

  if (!art || !Variant) {
    return <div className="w-full h-full bg-[#0A0A0A]" data-testid="art-fallback" />;
  }

  // key on momentId remounts the SVG so animations replay per moment
  return (
    <div className="w-full h-full bg-[#0A0A0A]" data-testid="commentary-art">
      <Variant key={momentId} still={Boolean(still)} art={art} />
    </div>
  );
}
